"use client";

import type { FC } from "react";
import { cn } from "utils";
import { EdHeroBubble } from "../quiz/EdHeroBubble";
import { QuizContinueButton } from "../quiz/QuizChoiceOption";
import { QuizProgressHeader, QuizStepLayout } from "../quiz/QuizStepLayout";
import { QUIZ_COMMON_COPY } from "../onboardingWizardCopy";
import type { QuizStepDefinition } from "../quizStepsConfig";
import { resolveQuizHeroMessage } from "../quizStepsConfig";

interface NumericQuizStepProps {
  definition: QuizStepDefinition;
  value: number;
  displayName: string;
  progressIndex: number;
  progressTotal: number;
  onBack: () => void;
  onChange: (value: number) => void;
  onContinue: () => void;
}

function formatSliderMaxLabel(max: number): string {
  if (max >= 1_000_000) {
    return `$${Math.round(max / 1_000_000)}M+`;
  }
  if (max >= 1_000) {
    return `$${Math.round(max / 1_000)}k+`;
  }
  return `$${max}+`;
}

export const NumericQuizStep: FC<NumericQuizStepProps> = ({
  definition,
  value,
  displayName,
  progressIndex,
  progressTotal,
  onBack,
  onChange,
  onContinue,
}) => {
  const numeric = definition.numeric;
  if (!numeric) {
    return null;
  }

  const formatted = numeric.format?.(value) ?? String(value);
  const hasValue = numeric.allowZero
    ? value >= numeric.min
    : value > numeric.min;
  const fillPercent =
    numeric.max === numeric.min
      ? 0
      : ((value - numeric.min) / (numeric.max - numeric.min)) * 100;
  const heroMessage = resolveQuizHeroMessage(
    definition,
    displayName,
    QUIZ_COMMON_COPY.greetingTemplate,
  );

  return (
    <QuizStepLayout
      testId={`onboarding-numeric-quiz-${definition.stepId}`}
      header={
        <QuizProgressHeader
          progressIndex={progressIndex}
          progressTotal={progressTotal}
          onBack={onBack}
        />
      }
      footer={<QuizContinueButton disabled={!hasValue} onClick={onContinue} />}
    >
      <div
        className="flex max-md:flex-1 flex-col gap-8 px-2 pb-7 pt-6 md:flex-none md:gap-[60px] md:px-0 md:pb-0 md:pt-0 max-md:overflow-y-auto md:overflow-visible no-scrollbar"
        data-testid="numeric-quiz-body"
        data-figma-node={
          hasValue
            ? (definition.figmaNodeIds[1] ?? definition.figmaNodeIds[0])
            : definition.figmaNodeIds[0]
        }
      >
        <EdHeroBubble message={heroMessage} />

        <h2
          className="headline_regular_m text-content_inverse_primary"
          data-testid="numeric-quiz-question"
        >
          {definition.question}
        </h2>

        <div
          className="flex max-md:min-h-[400px] max-md:flex-1 flex-col max-md:justify-center gap-6 md:flex-none"
          data-testid="numeric-quiz-value-block"
        >
          <div
            className="flex items-end gap-2"
            data-testid="numeric-quiz-display-row"
          >
            {numeric.prefix ? (
              <span
                className="flex w-[11px] shrink-0 items-center justify-center py-1.5 title_xl text-content_inverse_primary"
                data-testid="numeric-quiz-display-prefix"
              >
                {numeric.prefix}
              </span>
            ) : null}
            <p
              className={cn(
                "display_m border-b border-white py-1 font-semibold",
                hasValue
                  ? "text-content_aura_lv3_primary"
                  : "text-content_inverse_tertiary",
              )}
              data-testid="numeric-quiz-display"
            >
              {formatted}
            </p>
            {numeric.suffix ? (
              <span
                className="flex shrink-0 items-center py-1.5 title_xl text-content_inverse_secondary"
                data-testid="numeric-quiz-display-suffix"
              >
                {numeric.suffix}
              </span>
            ) : null}
          </div>

          <div
            className="flex flex-col gap-2.5"
            data-testid="numeric-quiz-slider-block"
          >
            <div
              className="relative h-7 w-full"
              data-testid="numeric-quiz-slider-shell"
            >
              <div
                className="pointer-events-none absolute inset-x-0 top-2.5 h-2 rounded bg-border_white_transparent10"
                data-testid="numeric-quiz-slider-track"
              />
              <div
                className="pointer-events-none absolute left-0 top-2.5 h-2 rounded bg-content_aura_lv3_primary"
                style={{ width: `${fillPercent}%` }}
                data-testid="numeric-quiz-slider-fill"
              />
              <input
                type="range"
                data-testid="numeric-quiz-slider"
                min={numeric.min}
                max={numeric.max}
                step={numeric.step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="numeric-quiz-range-input"
                aria-valuemin={numeric.min}
                aria-valuemax={numeric.max}
                aria-valuenow={value}
              />
            </div>
            <div
              className="body_medium_m flex justify-between text-content_inverse_primary opacity-70"
              data-testid="numeric-quiz-slider-labels"
            >
              <span data-testid="numeric-quiz-slider-min">
                {numeric.prefix ?? ""}
                {numeric.min === 0
                  ? "0"
                  : (numeric.format?.(numeric.min) ?? numeric.min)}
              </span>
              <span data-testid="numeric-quiz-slider-max">
                {formatSliderMaxLabel(numeric.max)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </QuizStepLayout>
  );
};
